import React, { useState, useMemo } from 'react';
import {
  Users, ArrowLeft, ArrowRight, Building2, Landmark, Factory, Wheat, Globe, Ruler,
  Camera, Vote, Handshake,
} from 'lucide-react';
import { capitalize, formatUSD, formatBRL, formatNum } from './constants';
import MayorBubble from './MayorBubble';

export default function CityIntroScreen({ cityDetail, selectedCity, selectedState, playerAvatar, onContinue }) {
  const [page, setPage] = useState(0);
  const prefeito = cityDetail?.politicos?.eleitos?.find(e => e.cargo === 'PREFEITO');
  const pib = cityDetail?.pib;
  const comercio = cityDetail?.comercio;

  const historia = cityDetail?.historia || '';
  const histChunks = useMemo(() => {
    if (!historia) return [];
    const sentences = historia.match(/[^.!?]+[.!?]+/g) || [historia];
    const result = [];
    let buf = '';
    for (const s of sentences) {
      if ((buf + s).length > 900 && buf) { result.push(buf.trim()); buf = s; }
      else buf += s;
    }
    if (buf.trim()) result.push(buf.trim());
    return result;
  }, [historia]);

  const slides = useMemo(() => {
    const s = [];
    s.push({ type: 'welcome' });
    if (histChunks.length > 0) {
      histChunks.forEach((text, i) => s.push({ type: 'history', text, first: i === 0, last: i === histChunks.length - 1 }));
    }
    s.push({ type: 'economy' });
    return s;
  }, [histChunks]);

  const current = slides[page] || slides[0];

  const allPhotos = useMemo(() => {
    const pool = [];
    if (cityDetail?.fotos) pool.push(...cityDetail.fotos);
    if (cityDetail?.fotosHistoricas) pool.push(...cityDetail.fotosHistoricas);
    return pool.filter(Boolean);
  }, [cityDetail]);

  const bannerPhoto = allPhotos.length > 0 ? allPhotos[page % allPhotos.length] : null;
  const economyIdx = slides.findIndex(s => s.type === 'economy');

  return (
    <div className="city-intro">
      {bannerPhoto && (
        <div className="city-intro__banner-photo">
          <img key={bannerPhoto} src={bannerPhoto} alt={selectedCity?.name}
            className="city-intro__banner-photo-img" />
          <div className="city-intro__banner-photo-gradient" />
          {current.type === 'history' && (
            <div className="city-intro__banner-photo-counter">
              <span className="city-intro__banner-photo-badge">
                <Camera size={8} /> {allPhotos.indexOf(bannerPhoto) + 1}/{allPhotos.length}
              </span>
            </div>
          )}
          <div className="city-intro__banner-text-wrap">
            <h2 className="city-intro__banner-title">
              {selectedCity?.name}
              <span className="city-intro__banner-state">, {selectedState?.sigla}</span>
            </h2>
            <div className="city-intro__banner-meta">
              {cityDetail?.gentilico && <span className="city-intro__banner-meta-italic">{cityDetail.gentilico}</span>}
              {cityDetail?.populacao > 0 && (
                <span className="city-intro__banner-meta-item"><Users size={10} /> {formatNum(cityDetail.populacao)} hab.</span>
              )}
              {cityDetail?.areaKm2 > 0 && (
                <span className="city-intro__banner-meta-item"><Ruler size={10} /> {formatNum(cityDetail.areaKm2)} km2</span>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="city-intro__content">

        {current.type === 'welcome' && (
          <MayorBubble prefeito={prefeito} selectedCity={selectedCity}>
            <p className="city-intro__text--mb">
              <span className="city-intro__text-highlight">Seja bem-vindo a {selectedCity?.name}! </span>
              {cityDetail?.gentilico
                ? `Aqui os moradores sao chamados de ${cityDetail.gentilico.toLowerCase()}.`
                : 'E uma honra recebe-lo em nossa cidade.'}
            </p>
            <div className="city-intro__kpi-grid">
              {cityDetail?.populacao > 0 && (
                <div className="city-intro__kpi-card">
                  <Users size={14} className="city-intro__kpi-icon city-intro__kpi-icon--blue" />
                  <p className="city-intro__kpi-value">{formatNum(cityDetail.populacao)}</p>
                  <p className="city-intro__kpi-label">Habitantes</p>
                </div>
              )}
              {cityDetail?.areaKm2 > 0 && (
                <div className="city-intro__kpi-card">
                  <Ruler size={14} className="city-intro__kpi-icon city-intro__kpi-icon--emerald" />
                  <p className="city-intro__kpi-value">{formatNum(cityDetail.areaKm2)}</p>
                  <p className="city-intro__kpi-label">km2</p>
                </div>
              )}
              {pib && (
                <div className="city-intro__kpi-card">
                  <Landmark size={14} className="city-intro__kpi-icon city-intro__kpi-icon--amber" />
                  <p className="city-intro__kpi-value">{formatBRL(pib.pibPerCapita)}</p>
                  <p className="city-intro__kpi-label">PIB/capita</p>
                </div>
              )}
              {cityDetail?.politicos && (
                <div className="city-intro__kpi-card">
                  <Vote size={14} className="city-intro__kpi-icon city-intro__kpi-icon--purple" />
                  <p className="city-intro__kpi-value">{cityDetail.politicos.eleitos.length}</p>
                  <p className="city-intro__kpi-label">Eleitos 2024</p>
                </div>
              )}
            </div>
            {cityDetail?.fotos?.length > 1 && (
              <div className="city-intro__photos-grid">
                {cityDetail.fotos.slice(0, 3).map((url, i) => (
                  <img key={i} src={url} alt={`${selectedCity?.name} ${i + 1}`}
                    className="city-intro__photo-thumb" />
                ))}
              </div>
            )}
          </MayorBubble>
        )}

        {current.type === 'history' && (
          <MayorBubble prefeito={prefeito} selectedCity={selectedCity}>
            <p className="city-intro__text-preline">
              {current.first && <span className="city-intro__text-highlight">Sobre a nossa historia... </span>}
              {current.text}
            </p>
            {cityDetail?.historiaFonte && current.last && (
              <p className="city-intro__text-source">Fonte: {cityDetail.historiaFonte}</p>
            )}
          </MayorBubble>
        )}

        {current.type === 'economy' && (
          <MayorBubble prefeito={prefeito} selectedCity={selectedCity}>
            <p className="city-intro__text--mb">
              <span className="city-intro__text-highlight">Vamos aos numeros da nossa economia. </span>
              {pib ? `Em ${pib.ano}, nosso PIB foi de ${formatBRL(pib.pibTotal * 1000)}.` : 'Conheca a realidade economica da nossa cidade.'}
            </p>

            <div className="city-intro__econ-grid">
              {pib && (
                <div className="city-intro__econ-card">
                  <p className="city-intro__econ-title">
                    <Landmark size={9} className="city-intro__econ-title-icon--blue" /> PIB ({pib.ano})
                  </p>
                  <div className="city-intro__econ-rows">
                    <div className="city-intro__econ-row">
                      <span className="city-intro__econ-label">Total</span>
                      <span className="city-intro__econ-value">{formatBRL(pib.pibTotal * 1000)}</span>
                    </div>
                    <div className="city-intro__econ-row">
                      <span className="city-intro__econ-label">Per capita</span>
                      <span className="city-intro__econ-value">{formatBRL(pib.pibPerCapita)}</span>
                    </div>
                    {pib.impostos > 0 && (
                      <div className="city-intro__econ-row">
                        <span className="city-intro__econ-label">Impostos</span>
                        <span className="city-intro__econ-value--sm">{formatBRL(pib.impostos * 1000)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {pib && pib.vabTotal > 0 && (
                <div className="city-intro__econ-card">
                  <p className="city-intro__econ-title">
                    <Factory size={9} className="city-intro__econ-title-icon--amber" /> Setores
                  </p>
                  <div className="city-intro__econ-rows">
                    {[
                      { label: 'Servicos', val: pib.vabServicos, color: '#60a5fa', Icon: Building2 },
                      { label: 'Industria', val: pib.vabIndustria, color: '#f59e0b', Icon: Factory },
                      { label: 'Agropecuaria', val: pib.vabAgro, color: '#34d399', Icon: Wheat },
                      { label: 'Adm. Publica', val: pib.vabAdmPublica, color: '#a78bfa', Icon: Landmark },
                    ].map(s => {
                      const pct = ((s.val / pib.vabTotal) * 100);
                      if (pct < 0.5) return null;
                      return (
                        <div key={s.label} className="city-intro__sector-row">
                          <s.Icon size={9} style={{ color: s.color }} className="city-intro__sector-icon" />
                          <span className="city-intro__sector-label">{s.label}</span>
                          <div className="city-intro__sector-bar">
                            <div className="city-intro__sector-bar-fill" style={{ width: `${pct}%`, background: s.color }} />
                          </div>
                          <span className="city-intro__sector-pct">{pct.toFixed(0)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {(comercio?.exportacao > 0 || comercio?.importacao > 0) && (
                <div className="city-intro__econ-card">
                  <p className="city-intro__econ-title">
                    <Globe size={9} className="city-intro__econ-title-icon--emerald" /> Comercio exterior
                  </p>
                  <div className="city-intro__econ-rows">
                    <div className="city-intro__econ-row">
                      <span className="city-intro__econ-label">Exportacoes</span>
                      <span className="city-intro__econ-value--green">{formatUSD(comercio.exportacao)}</span>
                    </div>
                    <div className="city-intro__econ-row">
                      <span className="city-intro__econ-label">Importacoes</span>
                      <span className="city-intro__econ-value--orange">{formatUSD(comercio.importacao)}</span>
                    </div>
                    <div className="city-intro__econ-row">
                      <span className="city-intro__econ-label">Saldo</span>
                      <span className={comercio.saldo >= 0 ? 'city-intro__econ-value--green-saldo' : 'city-intro__econ-value--red-saldo'}>{formatUSD(comercio.saldo)}</span>
                    </div>
                  </div>
                </div>
              )}

              {(comercio?.topExportacoes?.length > 0 || comercio?.topImportacoes?.length > 0) && (
                <div className="city-intro__econ-card">
                  <p className="city-intro__econ-title">
                    <Wheat size={9} className="city-intro__econ-title-icon--cyan" /> Principais produtos
                  </p>
                  <div className="city-intro__product-list">
                    {(comercio.topExportacoes || []).slice(0, 3).map((p, i) => (
                      <p key={`e${i}`} className="city-intro__product">
                        <span className="city-intro__product-tag--exp">EXP</span> {capitalize(p.produto)} <span className="city-intro__product-value">({formatUSD(p.valor)})</span>
                      </p>
                    ))}
                    {(comercio.topImportacoes || []).slice(0, 2).map((p, i) => (
                      <p key={`i${i}`} className="city-intro__product">
                        <span className="city-intro__product-tag--imp">IMP</span> {capitalize(p.produto)} <span className="city-intro__product-value">({formatUSD(p.valor)})</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!pib && !comercio?.exportacao && (
              <p className="city-intro__text-empty">Dados economicos nao disponiveis para este municipio.</p>
            )}
          </MayorBubble>
        )}

        {/* Navigation */}
        <div className="city-intro__nav-bar">
          <div className="city-intro__nav-dots-wrap">
            {slides.map((s, i) => (
              <div key={i} className="city-intro__nav-dot"
                onClick={() => setPage(i)}
                style={{
                  background: i === page ? 'rgba(212,165,116,.9)' : i < page ? 'rgba(212,165,116,.35)' : 'rgba(255,255,255,.1)',
                  transform: i === page ? 'scale(1.3)' : 'scale(1)',
                }} />
            ))}
            <span className="city-intro__nav-dot-label">
              {current.type === 'welcome' ? 'Apresentacao' : current.type === 'history' ? 'Historia' : 'Economia'}
            </span>
          </div>
          <div className="city-intro__nav-actions">
            {page > 0 && (
              <button onClick={() => setPage(page - 1)} className="city-intro__nav-btn-back">
                <ArrowLeft size={14} /> Voltar
              </button>
            )}
            {current.type === 'history' && economyIdx > 0 && (
              <button onClick={() => setPage(economyIdx)} className="city-intro__nav-btn-skip">
                Pular historia
              </button>
            )}
            {page < slides.length - 1 ? (
              <button onClick={() => setPage(page + 1)} className="city-intro__nav-btn-next"
                style={{ background: 'rgba(212,165,116,.9)', color: '#111' }}>
                Continuar <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={onContinue} className="city-intro__nav-btn-final"
                style={{ background: 'rgba(212,165,116,.95)', color: '#111' }}>
                <Handshake size={16} /> Escolher Vereador
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
